import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { isCappedOrFlexIOrUFPLS, isRegualr } from "./Helper/DrawdownDetailHelper";

import { drawdownDetailsMessage, LTADetailHeading, LTADetailTooltiopMessage } from "./Resource/Contents";

interface ILTABreakdownDetailsProps extends IProcessComponentProps {
    lessThan75: boolean;
    data: DrawdownModel;
    process: ProcessType;
    doCalculation: VoidFunction;
}

interface ILTABreakdownDetailsState {}

export class LTABreakdownDetails extends ProcessComponent<ILTABreakdownDetailsProps, ILTABreakdownDetailsState> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    private renderLTATextBox(): JSX.Element {
        const { data, onChange } = this.props;
        return (
            <PercentageTextBoxEditor
                cssWrapper="control-size-100"
                isNotForm={true}
                value={data?.SlaUsedOffPlatform}
                atLeast={{
                    errorMessage: drawdownDetailsMessage.LTAInputErrorMessage,
                    value: 0,
                }}
                lessThan={{ errorMessage: drawdownDetailsMessage.LTAMaxErrorMsg, value: 999.99 }}
                alwaysValidateOnUpdate={true}
                onChange={(v) => {
                    this.onDataChange(onChange, { SlaUsedOffPlatform: v }, data);
                }}
                onBlur={(v) => this.props.doCalculation()}
                maxNumberOfDecimalPlaces={{ errorMessage: "only allow two decimal places", value: 2 }}
                required={"Please enter"}
            />
        );
    }

    private renderLTADetails(): JSX.Element {
        const { lessThan75, data } = this.props;
        const LifetimeAllowance = data?.LifetimeAllowance;
        const LTADetails = [];
        if (lessThan75) {
            LTADetails.push(
                { title: LTADetailHeading.previousLTA, data: LifetimeAllowance?.SlaPreviouslyCrystallised + "%" },
                {
                    title: LTADetailHeading.LTABefore2006,
                    data: LifetimeAllowance?.SlaBenefitsPriorTo2006 + "%",
                    tooltip: LTADetailTooltiopMessage.previousLTA,
                }
            );
        }
        LTADetails.push({
            title: LTADetailHeading.otherLTA,
            data: this.renderLTATextBox(),
            tooltip: LTADetailTooltiopMessage.otherLTA,
        });
        if (lessThan75) {
            LTADetails.push({
                title: LTADetailHeading.availableLTA,
                data:
                    LifetimeAllowance?.AvailablePersonalAllowance?.Amount > 0
                        ? MoneyDisplayHelper.getMoneyFormat(LifetimeAllowance?.AvailablePersonalAllowance)
                        : "N/A",
            });
        }
        if (isCappedOrFlexIOrUFPLS(data) && lessThan75) {
            LTADetails.push(
                {
                    title: LTADetailHeading.LTAByThisCrystallisation,
                    data: LifetimeAllowance?.SlaUsedByProposedCrystallisation + "%",
                    tooltip: LTADetailTooltiopMessage.LTAByThisCrystallisation,
                },
                {
                    title: LTADetailHeading.LTAInTotal,
                    data: LifetimeAllowance?.TotalSlaUsed + "%",
                    tooltip: LTADetailTooltiopMessage.LTAInTotal,
                },
                {
                    title: LTADetailHeading.crystallisationAmountSubjectToLTAExcessCharge,
                    data: MoneyDisplayHelper.getMoneyFormat(LifetimeAllowance?.ChargeableCrystallisationPortion),
                }
            );
        }
        if (isRegualr(data) && lessThan75) {
            const previouslyUsed = LifetimeAllowance?.SlaPreviouslyCrystallised + data?.SlaUsedOffPlatform;
            const differentInitialCrystallisationAmount =
                this.props.data.RegularCrystallisationDetails != null
                    ? this.props.data.RegularCrystallisationDetails.HasADifferentInitialAmount
                    : false;
            if (differentInitialCrystallisationAmount) {
                LTADetails.push({
                    title: LTADetailHeading.estimateLTAForInitialCrystallisation,
                    data: LifetimeAllowance?.SlaUsedByProposedInitialRegularCrystallisation + "%",
                });
            }

            LTADetails.push(
                {
                    title: LTADetailHeading.estimateLTAPerRegularCrystallisation,
                    data: LifetimeAllowance?.SlaUsedByProposedCrystallisation + "%",
                },
                {
                    title: LTADetailHeading.estimateAvailableLTAInAYear,
                    data: Math.max(0, 100 - LifetimeAllowance?.EstimatedTotalSlaUsedAfterAYear) + "%",
                },
                {
                    title: LTADetailHeading.estimateLTAUsedInAYear,
                    data: LifetimeAllowance?.EstimatedTotalSlaUsedAfterAYear - previouslyUsed + "%",
                },
                {
                    title: LTADetailHeading.estimateTotalLTAUsedInAYear,
                    data: LifetimeAllowance?.EstimatedTotalSlaUsedAfterAYear + "%",
                },
                {
                    title: LTADetailHeading.estimatedChargeableCrystallisation,
                    data: Math.max(0, LifetimeAllowance?.EstimatedTotalSlaUsedAfterAYear - 100) + "%",
                    tooltip: LTADetailTooltiopMessage.estimatedChargeableCrystallisation,
                }
            );
        }

        return (
            <Accordion label="LTA breakdown details " cssWrapper="accordion-with-arrow mb-40" isOpen={true}>
                <Typography type="common" wrapCss="dt-bold wider pt-12" config={LTADetails} />
            </Accordion>
        );
    }

    public render(): JSX.Element {
        return <>{this.renderLTADetails()}</>;
    }
}
